import React, { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { MessageSquare, Check } from "lucide-react";
import Seo from "@/components/site/Seo";
import { CONTACT, BRAND } from "@/data/site";
import { breadcrumb } from "@/data/schema";

export default function Contact() {
  const [params] = useSearchParams();
  const type = params.get("type") || "general";
  const isTraining = type === "training";

  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [status, setStatus] = useState("idle"); // idle | sending | success | error

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const encode = (data) =>
    Object.keys(data)
      .map((k) => encodeURIComponent(k) + "=" + encodeURIComponent(data[k]))
      .join("&");

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      const res = await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: encode({ "form-name": "contact", "inquiry-type": type, ...form }),
      });
      if (!res.ok) throw new Error("bad status");
      setStatus("success");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      setStatus("error");
    }
  };

  return (
    <>
      <Seo
        title={`${isTraining ? "Training Inquiry" : "Contact"} | Golden Touch Lash & Brow`}
        description="Get in touch with Golden Touch Lash & Brow in Vero Beach, Florida to inquire about lash & brow training and services."
        path="/contact"
        jsonLd={[breadcrumb([
          { name: "Home", path: "/" },
          { name: "Contact", path: "/contact" },
        ])]}
      />

      <section className="bg-onyx pt-28 md:pt-36 pb-20">
        <div className="mx-auto max-w-2xl px-6 lg:px-10">
          <p className="eyebrow text-center">{isTraining ? "Training Inquiry" : "Get in Touch"}</p>
          <h1 className="mt-4 text-center font-display text-4xl md:text-5xl text-ivory">
            {isTraining ? "Inquire About Training" : "Contact Golden Touch"}
          </h1>
          <p className="mx-auto mt-5 max-w-lg text-center text-muted-ivory">
            {isTraining
              ? "Ready to learn from Golden Touch? Send us a note and we'll get back to you with class details and availability."
              : "Have a question? Send us a message and we'll get back to you as soon as possible."}
          </p>

          {status === "success" ? (
            <div className="mt-10 border border-gold/30 bg-charcoal p-10 text-center">
              <Check className="mx-auto text-gold" size={40} />
              <h2 className="mt-4 font-display text-2xl text-ivory">Thank you!</h2>
              <p className="mt-3 text-muted-ivory">
                Your message has been received. We'll be in touch soon.
              </p>
              <Link
                to="/"
                className="btn-gold-outline mt-8 inline-flex px-8 py-3 text-[0.72rem] tracking-[0.24em] uppercase"
              >
                Back Home
              </Link>
            </div>
          ) : (
            <form
              onSubmit={submit}
              name="contact"
              method="POST"
              data-netlify="true"
              netlify-honeypot="bot-field"
              className="mt-10 space-y-5"
              noValidate
            >
              <input type="hidden" name="form-name" value="contact" />
              <input type="hidden" name="inquiry-type" value={type} />
              <p className="hidden">
                <label>Don't fill this out: <input name="bot-field" /></label>
              </p>
              <div>
                <label htmlFor="name" className="block text-[0.7rem] uppercase tracking-[0.2em] text-gold">
                  Name
                </label>
                <input
                  id="name" name="name" type="text" required value={form.name} onChange={update("name")}
                  className="mt-2 w-full border border-gold/25 bg-charcoal px-4 py-3 text-ivory placeholder:text-muted-ivory focus:border-gold focus:outline-none"
                  placeholder="Your name"
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-[0.7rem] uppercase tracking-[0.2em] text-gold">
                  Email
                </label>
                <input
                  id="email" name="email" type="email" required value={form.email} onChange={update("email")}
                  className="mt-2 w-full border border-gold/25 bg-charcoal px-4 py-3 text-ivory placeholder:text-muted-ivory focus:border-gold focus:outline-none"
                  placeholder="you@email.com"
                />
              </div>
              <div>
                <label htmlFor="phone" className="block text-[0.7rem] uppercase tracking-[0.2em] text-gold">
                  Phone (optional)
                </label>
                <input
                  id="phone" name="phone" type="tel" value={form.phone} onChange={update("phone")}
                  className="mt-2 w-full border border-gold/25 bg-charcoal px-4 py-3 text-ivory placeholder:text-muted-ivory focus:border-gold focus:outline-none"
                  placeholder="Best number to reach you"
                />
              </div>
              <div>
                <label htmlFor="message" className="block text-[0.7rem] uppercase tracking-[0.2em] text-gold">
                  Message
                </label>
                <textarea
                  id="message" name="message" required rows={5} value={form.message} onChange={update("message")}
                  className="mt-2 w-full border border-gold/25 bg-charcoal px-4 py-3 text-ivory placeholder:text-muted-ivory focus:border-gold focus:outline-none"
                  placeholder={isTraining ? "Tell us what you'd like to learn..." : "How can we help?"}
                />
              </div>

              {status === "error" && (
                <p className="text-sm text-red-400">
                  Something went wrong. Please try again, or text us at {CONTACT.phoneDisplay}.
                </p>
              )}

              <button
                type="submit"
                disabled={status === "sending"}
                className="btn-gold w-full px-8 py-4 text-[0.72rem] tracking-[0.24em] uppercase disabled:opacity-60"
              >
                {status === "sending" ? "Sending..." : "Send Message"}
              </button>

              <p className="text-center text-xs text-muted-ivory">
                Prefer to text? Reach {BRAND.shortName} at{" "}
                <a href={CONTACT.smsHref} className="inline-flex items-center gap-1 text-gold hover:underline">
                  <MessageSquare size={13} /> {CONTACT.phoneDisplay}
                </a>
              </p>
            </form>
          )}
        </div>
      </section>
    </>
  );
}
